import React from 'react';
import { Pressable, View } from 'react-native';
import { Link, type Href } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Text } from '@/components/gluestack/text';
import { Skeleton } from '@/components/gluestack/skeleton';
import { ProfilePicture } from '@/components/domain/common/ProfilePicture';
import { EventCard, EventCardSkeleton } from '@/components/domain/events/EventCard';
import { useFormatCount } from '@/hooks/useFormatCount';
import { MembersService } from '~/services/domain/MembersService';
import { EventsService } from '~/services/domain/EventsService';

const ONLINE_MEMBERS_LIMIT = 24;
const PROFILE_VISITORS_LIMIT = 12;
const UPCOMING_EVENTS_LIMIT = 4;

interface SectionProps {
  title: string;
  count?: number;
  myTypeCount?: number;
  href?: Href;
  children: React.ReactNode;
  containerClassName?: string;
}

interface CountBadgeProps {
  count?: number;
  suffix?: string;
  outline?: boolean;
}

const CountBadge = ({ count, suffix, outline = false }: CountBadgeProps) => {
  const { formatCount } = useFormatCount();

  if (!count || count <= 0) return null;

  const containerClassName = outline
    ? 'ml-2 items-center justify-center rounded-full border border-tertiary-500 px-2 py-0.5'
    : 'ml-2 items-center justify-center rounded-full bg-tertiary-500 px-2 py-0.5';
  const textColor = outline ? 'text-tertiary-500' : 'text-white';

  return (
    <View className={containerClassName}>
      {suffix ? (
        <View className="flex-row items-center gap-1">
          <Text size="xs" bold className={textColor}>
            {formatCount(count)}
          </Text>
          <Text size="xs" className={textColor}>
            {suffix}
          </Text>
        </View>
      ) : (
        <Text size="xs" bold className={textColor}>
          {formatCount(count)}
        </Text>
      )}
    </View>
  );
};

const Section = ({
  title,
  count,
  myTypeCount,
  href,
  children,
  containerClassName = 'flex-row flex-wrap',
}: SectionProps) => {
  const { t } = useTranslation();

  const headerContent = (
    <>
      <Text
        className={`text-sm font-bold uppercase text-tertiary-500 ${
          href ? 'web:hover:underline' : ''
        }`}>
        {title}
      </Text>
      <CountBadge count={count} />
      <CountBadge count={myTypeCount} suffix={t('common.filters.myType')} outline />
    </>
  );

  return (
    <View>
      {href ? (
        <Link href={href} asChild>
          <Pressable className="mb-4 flex-row items-center self-start web:cursor-pointer">
            {headerContent}
          </Pressable>
        </Link>
      ) : (
        <View className="mb-4 flex-row items-center self-start">{headerContent}</View>
      )}
      <View className={containerClassName}>{children}</View>
    </View>
  );
};

export function FeedRightPanel() {
  const { t } = useTranslation();

  const { data: onlineMembers, isPending: isOnlineMembersPending } = useQuery({
    queryKey: ['members', 'online', 'rightPanel'],
    queryFn: async () =>
      MembersService.getOnlineMembers({
        take: ONLINE_MEMBERS_LIMIT,
        includeUnfilteredItemCount: true,
      }),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
  });

  const { data: profileVisitors, isPending: isProfileVisitorsPending } = useQuery({
    queryKey: ['members', 'visitors', 'rightPanel'],
    queryFn: () =>
      MembersService.getVisitorMembers({
        take: PROFILE_VISITORS_LIMIT,
        includeUnfilteredItemCount: true,
      }),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
  });

  const { data: upcomingEvents, isPending: isUpcomingEventsPending } = useQuery({
    queryKey: ['events', 'upcoming', 'rightPanel'],
    queryFn: () => EventsService.getUpcomingEvents({ take: UPCOMING_EVENTS_LIMIT }),
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: true,
  });

  const members = onlineMembers?.MembersBasic?.slice(0, ONLINE_MEMBERS_LIMIT) ?? [];
  const isOnlineMyTypeFiltered = onlineMembers?.IsMyTypeFiltered === true;
  const hasUnfilteredOnlineCount = typeof onlineMembers?.UnfilteredItemCount === 'number';
  const onlineCount = isOnlineMyTypeFiltered
    ? hasUnfilteredOnlineCount
      ? onlineMembers.UnfilteredItemCount
      : undefined
    : onlineMembers?.ItemCount;
  const onlineMyTypeCount =
    isOnlineMyTypeFiltered ? onlineMembers?.ItemCount : undefined;
  const visitors = profileVisitors?.MembersBasic?.slice(0, PROFILE_VISITORS_LIMIT) ?? [];
  const isVisitorsMyTypeFiltered = profileVisitors?.IsMyTypeFiltered === true;
  const hasUnfilteredVisitorsCount = typeof profileVisitors?.UnfilteredItemCount === 'number';
  const visitorsCount = isVisitorsMyTypeFiltered
    ? hasUnfilteredVisitorsCount
      ? profileVisitors.UnfilteredItemCount
      : undefined
    : profileVisitors?.ItemCount;
  const visitorsMyTypeCount =
    isVisitorsMyTypeFiltered ? profileVisitors?.ItemCount : undefined;
  const events = upcomingEvents?.EventsItems?.slice(0, UPCOMING_EVENTS_LIMIT) ?? [];
  const upcomingEventsCount = upcomingEvents?.ItemCount ?? 0;

  const hasAnyContent = members.length > 0 || visitors.length > 0 || events.length > 0;

  if (
    !hasAnyContent &&
    !isOnlineMembersPending &&
    !isProfileVisitorsPending &&
    !isUpcomingEventsPending
  )
    return null;

  return (
    <View className="gap-6 android:pb-8">
      {isOnlineMembersPending ? (
        <Section
          title={t('feeds.onlineMembers')}
          containerClassName="-m-1 flex-row flex-wrap">
          {Array.from({ length: ONLINE_MEMBERS_LIMIT }).map((_, index) => (
            <View key={`online-skeleton-${index}`} className="w-[16.6%] p-1">
              <Skeleton className="aspect-square w-full rounded-xl" />
            </View>
          ))}
        </Section>
      ) : (
        members.length > 0 && (
          <Section
            title={t('feeds.onlineMembers')}
            count={onlineCount}
            myTypeCount={onlineMyTypeCount}
            href="/people/online"
            containerClassName="-m-1 mb-2 flex-row flex-wrap">
            {members.map((member) => (
              <View key={`online-${member.Id}`} className="w-[16.6%] p-1">
                <ProfilePicture
                  member={member}
                  size="custom"
                  showPresence={false}
                  showTooltip
                  className="aspect-square w-full !rounded-xl"
                  imageClassName="!rounded-xl"
                />
              </View>
            ))}
          </Section>
        )
      )}

      {isProfileVisitorsPending ? (
        <Section
          title={t('feeds.profileVisitors')}
          containerClassName="-m-1 flex-row flex-wrap">
          {Array.from({ length: PROFILE_VISITORS_LIMIT }).map((_, index) => (
            <View key={`visitor-skeleton-${index}`} className="w-[16.6%] p-1">
              <Skeleton className="aspect-square w-full rounded-xl" />
            </View>
          ))}
        </Section>
      ) : (
        visitors.length > 0 && (
          <Section
            title={t('feeds.profileVisitors')}
            count={visitorsCount}
            myTypeCount={visitorsMyTypeCount}
            href="/people/visitors"
            containerClassName="-m-1 mb-2 flex-row flex-wrap">
            {visitors.map((member) => (
              <View key={`visitor-${member.Id}`} className="w-[16.6%] p-1">
                <ProfilePicture
                  member={member}
                  size="custom"
                  showPresence={false}
                  showTooltip
                  className="aspect-square w-full !rounded-xl"
                  imageClassName="!rounded-xl"
                />
              </View>
            ))}
          </Section>
        )
      )}

      {isUpcomingEventsPending ? (
        <Section title={t('events.upcoming')} containerClassName="-m-2 flex-row flex-wrap">
          {Array.from({ length: UPCOMING_EVENTS_LIMIT }).map((_, index) => (
            <View key={`event-skeleton-${index}`} className="w-1/2 p-2">
              <EventCardSkeleton />
            </View>
          ))}
        </Section>
      ) : (
        events.length > 0 && (
          <Section
            title={t('events.upcoming')}
            count={upcomingEventsCount}
            href="/events/upcoming"
            containerClassName="-m-2 flex-row flex-wrap">
            {events.map((event) => (
              <View key={`event-${event.Id}`} className="w-1/2 p-2">
                <EventCard event={event} compact className="bg-background-50" />
              </View>
            ))}
          </Section>
        )
      )}
    </View>
  );
}
